import React from "react"
import { accountTypes } from "components/accounts/accountTypes"

export const benefitTypes = {
  "401k_plan": {
    label: "401(k) Retirement Plan",
    worksheetType: "RetirementPlan",
    cardType: "RetirementPlan",
    description: (
      <span>
        A 401(k) plan is a tax-qualified, defined-contribution retirement plan.
        Under the plan, retirement contributions are deducted from the
        employee's paycheck and not subject to taxes until withdrawn, usually at
        retirement, or unless directed to the Roth option, if any. In addition
        to the tax savings, an employer may match a portion of the employees
        contribution to further amplify the employee's savings.
      </span>
    ),
    category: "benefit",
    accountOptionFilters: {
      connectedAccount: (account, person_id) => account =>
        account.type === "401k" && account.person_id === person_id,
      rothAccount: (account, person_id) => account =>
        account.type === "roth401k" && account.person_id === person_id,
      connectedAccountData: accountTypes["401k"],
      rothAccountData: accountTypes["roth401k"]
    }
  },

  "403b_plan": {
    label: "403(b) Retirement Plan",
    worksheetType: "RetirementPlan",
    cardType: "RetirementPlan",
    description: (
      <span>
        A 403(b) plan is a tax-qualified, defined-contribution retirement plan
        much like a 401(k) plan. Under the plan, retirement contributions are
        deducted from the employee's paycheck and not subject to taxes until
        withdrawn, usually at retirement, or unless directed to the Roth option,
        if any. In addition to the tax savings, an employer may match a portion
        of the employees contribution to further amplify the employee's savings.
      </span>
    ),
    category: "benefit",
    accountOptionFilters: {
      connectedAccount: (account, person_id) => account =>
        account.type === "403b" && account.person_id === person_id,
      rothAccount: (account, person_id) => account =>
        account.type === "roth403b" && account.person_id === person_id,
      connectedAccountData: accountTypes["403b"],
      rothAccountData: accountTypes["roth403b"]
    }
  },

  "457b_plan": {
    label: "457(b) Retirement Plan",
    worksheetType: "RetirementPlan",
    cardType: "RetirementPlan",
    description: (
      <span>
        A 457(b) plan is a tax-qualified, defined-contribution retirement plan
        much like a 401(k) plan. Under the plan, retirement contributions are
        deducted from the employee's paycheck and not subject to taxes until
        withdrawn, usually at retirement, or unless directed to the Roth option,
        if any. In addition to the tax savings, an employer may match a portion
        of the employees contribution to further amplify the employee's savings.
      </span>
    ),
    category: "benefit",
    accountOptionFilters: {
      connectedAccount: (account, person_id) => account =>
        account.type === "457b" && account.person_id === person_id,
      rothAccount: (account, person_id) => account =>
        account.type === "roth457b" && account.person_id === person_id,
      connectedAccountData: accountTypes["457b"],
      rothAccountData: accountTypes["roth457b"]
    }
  },
  sep_plan: {
    label: "SEP Retirement Plan",
    worksheetType: "SEPPlan",
    cardType: "SEPPlan",
    description: (
      <span>
        A Simplified Employee Pension (SEP) plan is a simple way for an employer
        to contribute toward their employees' retirement savings. Contributions
        are made to an Individual Retirement Account (IRA) set up for each plan
        participant (a SEP-IRA).
      </span>
    ),
    category: "benefit",
    accountOptionFilters: {
      connectedAccount: (account, person_id) => account =>
        account.type === "sepira" && account.person_id === person_id,
      connectedAccountData: accountTypes["sepira"]
    }
  },
  socialsecurity_plan: {
    label: "Social Security",
    worksheetType: "SocialSecurity",
    cardType: "SocialSecurityPlan",
    description: (
      <span>
        Your future Social Security benefit. We'll estimate this for you based
        on you current income, but if your current income doesn't accurately
        reflect your earnings history you'll want to add it here.
      </span>
    ),
    category: "benefit",
    validator: ({ incomes, people, benefits }) => {
      const incomePeopleIds = incomes
        .filter(({ type }) => type === "socialsecurity")
        .map(({ person_id }) => person_id)
      const benefitPeopleIds = benefits
        .filter(({ type }) => type === "socialsecurity_plan")
        .map(({ person_id }) => person_id)
      const eligiblePeopleIds = people
        .filter(({ role }) => role === "spouse" || role === "client")
        .filter(({ id }) => !incomePeopleIds.includes(id))
        .filter(({ id }) => !benefitPeopleIds.includes(id))
        .map(({ id }) => id)
      return eligiblePeopleIds.length > 0
    }
  },
  pension_plan: {
    label: "Pension Plan",
    worksheetType: "PensionPlan",
    cardType: "PensionPlan",
    description: (
      <span>
        Your future pension benefit. You can enter your defined benefit pension
        plan to capture the fixed monthly payment you expect to start receiving
        at retirement.
      </span>
    ),
    category: "benefit"
  },
  grouplife_plan: {
    label: "Group Life Insurance",
    worksheetType: "GroupLifePlan",
    cardType: "GroupLifePlan",
    description: (
      <span>
        An employer-sponsored group term life insurance benefit. You can add any
        life insurance coverage you've elected for you or your spouse through
        your employer.
      </span>
    ),
    category: "benefit"
  },
  ...(process.env.REACT_APP_DEPLOY_ENV !== "production" && {
    mxhc_plan: {
      label: "Marketplace/Exchange Healthcare Plan",
      worksheetType: "MXHCPlan",
      cardType: "MXHCPlan",
      description: (
        <span>
          A healthcare plan purchased outside an employer on a public health
          insurance exchange or marketplace like HealthCare.gov
        </span>
      ),
      category: "benefit"
    }
  }),
  eshc_plan: {
    label: "Health Care Plan",
    worksheetType: "ESHCPlan",
    cardType: "ESHCPlan",
    description: (
      <span>Your employer may provide a healthcare plan as a benefit.</span>
    ),
    category: "benefit",
    accountOptionFilters: {
      hsaAccount: (account, person_id) => account =>
        account.type === "hsa" && account.person_id === person_id
    }
  }
}
